import React from 'react';
import { ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { PageSelector } from '../shared/PageSelector';
import type { PricingPlan } from './PricingEditor';

interface PricingPlanCardProps {
  plan: PricingPlan;
  index: number;
  totalPlans: number;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  onUpdate: (updates: Partial<PricingPlan>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
  onSetHighlighted: () => void;
}

export const PricingPlanCard: React.FC<PricingPlanCardProps> = ({
  plan,
  index,
  totalPlans,
  pages,
  isLoadingPages,
  currentPageComponents,
  onUpdate,
  onRemove,
  onMove,
  onSetHighlighted,
}) => {
  const addFeature = () => {
    onUpdate({ features: [...(plan.features || []), 'New Feature'] });
  };

  const updateFeature = (featureIndex: number, value: string) => {
    const features = [...(plan.features || [])];
    features[featureIndex] = value;
    onUpdate({ features });
  };

  const removeFeature = (featureIndex: number) => {
    const features = [...(plan.features || [])];
    features.splice(featureIndex, 1);
    onUpdate({ features });
  };

  return (
    <div
      className={`border rounded-lg p-4 ${
        plan.highlighted ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h4 className="text-lg font-medium text-gray-900">Plan {index + 1}</h4>
          {plan.highlighted && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Highlighted
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={onSetHighlighted}
            className={`px-3 py-1 text-sm rounded-md ${
              plan.highlighted
                ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {plan.highlighted ? 'Remove Highlight' : 'Make Highlighted'}
          </button>
          <button
            type="button"
            onClick={() => onMove('up')}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <ChevronUp size={16} />
          </button>
          <button
            type="button"
            onClick={() => onMove('down')}
            disabled={index === totalPlans - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <ChevronDown size={16} />
          </button>
          <button type="button" onClick={onRemove} className="p-1 text-red-400 hover:text-red-600">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Plan Basic Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Plan Name</label>
          <input
            type="text"
            value={plan.name || ''}
            onChange={e => onUpdate({ name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Plan name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <input
            type="text"
            value={plan.description || ''}
            onChange={e => onUpdate({ description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Plan description"
          />
        </div>
      </div>

      {/* Pricing */}
      <PricingSection plan={plan} onUpdate={onUpdate} />

      {/* Highlight Label */}
      {plan.highlighted && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Highlight Label (appears above highlighted plan)
          </label>
          <input
            type="text"
            value={plan.highlightLabel || ''}
            onChange={e => onUpdate({ highlightLabel: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Most Popular"
          />
        </div>
      )}

      {/* Badge */}
      <BadgeSection plan={plan} onUpdate={onUpdate} />

      {/* Features */}
      <FeaturesSection
        features={plan.features || []}
        onAddFeature={addFeature}
        onUpdateFeature={updateFeature}
        onRemoveFeature={removeFeature}
      />

      {/* CTA Button */}
      <CtaButtonSection
        plan={plan}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        onUpdate={onUpdate}
      />
    </div>
  );
};

// Sub-components

interface PricingSectionProps {
  plan: PricingPlan;
  onUpdate: (updates: Partial<PricingPlan>) => void;
}

const PricingSection: React.FC<PricingSectionProps> = ({ plan, onUpdate }) => (
  <div className="grid grid-cols-4 gap-4 mb-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
      <input
        type="number"
        value={plan.price?.amount || 0}
        onChange={e =>
          onUpdate({ price: { ...plan.price, amount: parseFloat(e.target.value) || 0 } })
        }
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        placeholder="0"
        min="0"
        step="0.01"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
      <select
        value={plan.price?.currency || 'USD'}
        onChange={e => onUpdate({ price: { ...plan.price, currency: e.target.value } })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
      >
        <option value="USD">USD ($)</option>
        <option value="EUR">EUR (€)</option>
        <option value="GBP">GBP (£)</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
      <select
        value={plan.price?.period || 'month'}
        onChange={e => onUpdate({ price: { ...plan.price, period: e.target.value } })}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
      >
        <option value="month">Month</option>
        <option value="year">Year</option>
        <option value="week">Week</option>
        <option value="day">Day</option>
        <option value="one-time">One-time</option>
      </select>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Original Price</label>
      <input
        type="number"
        value={plan.price?.originalAmount || ''}
        onChange={e =>
          onUpdate({
            price: {
              ...plan.price,
              originalAmount: e.target.value ? parseFloat(e.target.value) : undefined,
            },
          })
        }
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        placeholder="Original price"
        min="0"
        step="0.01"
      />
    </div>
  </div>
);

interface BadgeSectionProps {
  plan: PricingPlan;
  onUpdate: (updates: Partial<PricingPlan>) => void;
}

const BadgeSection: React.FC<BadgeSectionProps> = ({ plan, onUpdate }) => (
  <div className="mb-4">
    <div className="flex items-center justify-between mb-2">
      <label className="block text-sm font-medium text-gray-700">Badge (optional)</label>
      {plan.badge && (
        <button
          type="button"
          onClick={() => onUpdate({ badge: undefined })}
          className="text-sm text-red-600 hover:text-red-800"
        >
          Remove Badge
        </button>
      )}
    </div>

    {plan.badge ? (
      <div className="grid grid-cols-3 gap-4">
        <div>
          <input
            type="text"
            value={plan.badge.text || ''}
            onChange={e => onUpdate({ badge: { ...plan.badge!, text: e.target.value } })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Badge text"
          />
        </div>
        <div>
          <input
            type="color"
            value={plan.badge.color || '#f59e0b'}
            onChange={e => onUpdate({ badge: { ...plan.badge!, color: e.target.value } })}
            className="w-full h-10 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <input
            type="color"
            value={plan.badge.textColor || '#ffffff'}
            onChange={e => onUpdate({ badge: { ...plan.badge!, textColor: e.target.value } })}
            className="w-full h-10 border border-gray-300 rounded-md"
          />
        </div>
      </div>
    ) : (
      <button
        type="button"
        onClick={() =>
          onUpdate({ badge: { text: 'SAVE 25%', color: '#f59e0b', textColor: '#ffffff' } })
        }
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
      >
        Add Badge
      </button>
    )}
  </div>
);

interface FeaturesSectionProps {
  features: string[];
  onAddFeature: () => void;
  onUpdateFeature: (index: number, value: string) => void;
  onRemoveFeature: (index: number) => void;
}

const FeaturesSection: React.FC<FeaturesSectionProps> = ({
  features,
  onAddFeature,
  onUpdateFeature,
  onRemoveFeature,
}) => (
  <div className="mb-4">
    <div className="flex items-center justify-between mb-2">
      <label className="block text-sm font-medium text-gray-700">Features</label>
      <button
        type="button"
        onClick={onAddFeature}
        className="text-sm text-primary-600 hover:text-primary-800"
      >
        Add Feature
      </button>
    </div>
    <div className="space-y-2">
      {features.map((feature, featureIndex) => (
        <div key={featureIndex} className="flex items-center space-x-2">
          <input
            type="text"
            value={feature}
            onChange={e => onUpdateFeature(featureIndex, e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Feature description"
          />
          <button
            type="button"
            onClick={() => onRemoveFeature(featureIndex)}
            className="p-2 text-red-400 hover:text-red-600"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
    </div>
  </div>
);

interface CtaButtonSectionProps {
  plan: PricingPlan;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  onUpdate: (updates: Partial<PricingPlan>) => void;
}

const CtaButtonSection: React.FC<CtaButtonSectionProps> = ({
  plan,
  pages,
  isLoadingPages,
  currentPageComponents,
  onUpdate,
}) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
        <input
          type="text"
          value={plan.ctaButton?.text || ''}
          onChange={e => onUpdate({ ctaButton: { ...plan.ctaButton, text: e.target.value } })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Get Started"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Button Style</label>
        <select
          value={plan.ctaButton?.style || 'primary'}
          onChange={e => onUpdate({ ctaButton: { ...plan.ctaButton, style: e.target.value } })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="primary">Primary</option>
          <option value="secondary">Secondary</option>
          <option value="outline">Outline</option>
          <option value="ghost">Ghost</option>
        </select>
      </div>
    </div>

    <div className="mt-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">Button URL</label>
      <PageSelector
        value={plan.ctaButton?.url || ''}
        onChange={url => onUpdate({ ctaButton: { ...plan.ctaButton, url } })}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
      />
    </div>

    <div className="mt-4">
      <label className="flex items-center">
        <input
          type="checkbox"
          checked={plan.ctaButton?.target === '_blank'}
          onChange={e =>
            onUpdate({
              ctaButton: { ...plan.ctaButton, target: e.target.checked ? '_blank' : '_self' },
            })
          }
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="ml-2 text-sm text-gray-700">Open in new tab</span>
      </label>
    </div>
  </>
);
